import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  IconButton,
  alpha,
  Card,
  CardContent,
} from '@mui/material';
import {
  TrendingUp,
  NewReleases,
  Whatshot,
  People,
  ArrowForward,
  Star,
  MoreVert,
  Groups,
  ChatBubbleOutline,
  ArrowForwardIos,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';
import { communityApi } from '../../services/communityApi';
import api from '../../services/communityApi';
import toast from 'react-hot-toast';
import TopCommunitiesListModal from './TopCommunitiesListModal';
import CommunityChatModal from '../Chat/ChatModal'; 
import LoginRequiredModal from '../community/LoginRequiredModal'; 
import CommunityChatImage from '../../assets/CommunityChat.jpg';
import { CommunitySidebarSkeleton } from '../skeletons';

interface TrendingCommunity {
  id: string;
  name: string;
  slug: string;
  description: string;
  category: string;
  membersCount: number;
  postsCount: number;
  profilePicture?: string;
  growth: number;
}

interface TopContributor {
  id: string;
  username: string;
  profilePicture?: string;
  postsCount: number;
  latestPost?: string;
}

const CommunitySidebar: React.FC = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { mode } = useCustomTheme();

  const [trendingCommunities, setTrendingCommunities] = useState<TrendingCommunity[]>([]);
  const [allTrendingCommunities, setAllTrendingCommunities] = useState<TrendingCommunity[]>([]);
  const [topContributors, setTopContributors] = useState<TopContributor[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  // Theme Constants
  const isDark = mode === 'dark';
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';

  // Check if user is authenticated
  const isAuthenticated = !!user && !!token;

  const glassEffect = {
    background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(20px)',
    border: isDark 
      ? '1px solid rgba(196, 182, 111, 0.2)' 
      : '1px solid rgba(5, 24, 20, 0.1)',
    boxShadow: isDark 
      ? '0 20px 60px rgba(0,0,0,0.7)' 
      : '0 20px 60px rgba(5, 24, 20, 0.1)',
  };

  // Function to handle user profile click
  const handleUserProfileClick = (username: string, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation(); // Prevent event bubbling
    }
    if (username) {
      navigate(`/${username}`);
    }
  };

  // Fetch ALL trending communities for the dialog
  const fetchAllTrendingCommunities = async () => {
    try {
      const response = await communityApi.getCommunities({
        sortBy: 'popular',
        page: 1,
        limit: 50
      });
      
      if (response.success && response.communities) {
        const communities = response.communities.map(community => ({
          id: community.id,
          name: community.name,
          slug: community.slug,
          description: community.description,
          category: community.category,
          membersCount: community.membersCount || 0,
          postsCount: community.postsCount || 0,
          profilePicture: community.profilePicture,
          growth: Math.floor(Math.random() * 30) + 10
        }));
        
        setAllTrendingCommunities(communities);
      }
    } catch (error) {
      console.error('Failed to fetch all trending communities:', error);
    }
  };

  // Fetch trending communities (top 3 for sidebar)
  const fetchTrendingCommunities = async () => {
    try {
      const response = await communityApi.getCommunities({
        sortBy: 'popular',
        page: 1,
        limit: 3
      });
      
      if (response.success && response.communities) {
        const communities = response.communities.map(community => ({
          id: community.id,
          name: community.name,
          slug: community.slug,
          description: community.description,
          category: community.category,
          membersCount: community.membersCount || 0,
          postsCount: community.postsCount || 0,
          profilePicture: community.profilePicture,
          growth: Math.floor(Math.random() * 30) + 10
        }));
        
        setTrendingCommunities(communities);
      }
    } catch (error) {
      console.error('Failed to fetch trending communities:', error);
      toast.error('Failed to load trending communities');
    }
  };

  // Fetch top contributors based on post count
  const fetchTopContributors = async () => {
    try {
      const response = await api.get('/api/posts', {
        params: {
          limit: 100,
          page: 1
        }
      });
      
      if (response.data.success && response.data.posts) {
        const postCountByAuthor: { [key: string]: { 
          count: number; 
          username: string; 
          profilePicture?: string; 
          latestPost?: string 
        } } = {};
        
        response.data.posts.forEach((post: any) => {
          if (post.author) {
            const authorId = post.author.id;
            if (!postCountByAuthor[authorId]) {
              postCountByAuthor[authorId] = {
                count: 0,
                username: post.author.username,
                profilePicture: post.author.profilePicture,
                latestPost: post.createdAt
              };
            }
            postCountByAuthor[authorId].count++;
            
            if (post.createdAt > (postCountByAuthor[authorId].latestPost || '')) {
              postCountByAuthor[authorId].latestPost = post.createdAt;
            }
          }
        });
        
        const contributorsArray = Object.entries(postCountByAuthor)
          .map(([id, data]) => ({
            id,
            username: data.username,
            profilePicture: data.profilePicture,
            postsCount: data.count,
            latestPost: data.latestPost
          }))
          .sort((a, b) => b.postsCount - a.postsCount)
          .slice(0, 3);
        
        setTopContributors(contributorsArray);
      }
    } catch (error) {
      console.error('Failed to fetch top contributors:', error);
      setTopContributors([]);
    }
  };

  const fetchTrendingData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchTrendingCommunities(),
        fetchTopContributors(),
        fetchAllTrendingCommunities()
      ]);
    } catch (error) {
      console.error('Failed to fetch trending data:', error);
      toast.error('Failed to load trending data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrendingData();
  }, []);

  const handleCommunityClick = (slug: string) => {
    navigate(`/community/${slug}`);
  };

  const handleViewAllTrending = () => {
    setViewAllOpen(true);
  };

  const handleCloseViewAll = () => {
    setViewAllOpen(false);
  };

  const handleChatCardClick = () => {
    if (!isAuthenticated) {
      setLoginModalOpen(true);
    } else {
      setChatOpen(true);
    }
  };

  const handleCloseChat = () => {
    setChatOpen(false);
  };

  if (loading) {
    return <CommunitySidebarSkeleton />;
  }

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%', minWidth: 0 }}>
        
        {/* Trending Communities Card */}
        <Card sx={{ borderRadius: '24px', ...glassEffect, alignItems: 'center', width: '100%' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1.1rem', md: '0.95rem', lg: '1.1rem' }, color: isDark ? '#fff' : deepGreen, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', '& > svg': { flexShrink: 0 } }}>
                <TrendingUp sx={{ color: goldColor }} />
                Trending Communities
              </Typography>
              <IconButton size="small" onClick={handleViewAllTrending} sx={{ flexShrink: 0 }}>
                <MoreVert sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }} />
              </IconButton>
            </Box>

            <List disablePadding>
              {trendingCommunities.map((community, index) => (
                <ListItem
                  key={community.id}
                  sx={{ 
                    px: 1, 
                    py: 1.5,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                    }
                  }}
                  onClick={() => handleCommunityClick(community.slug)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        mr: 2,
                        color: isDark ? goldColor : deepGreen,
                        minWidth: '20px',
                        textAlign: 'center',
                        fontSize: '1rem',
                        fontWeight: 900
                      }}
                    >
                      {index + 1}
                    </Typography>
                    
                    <ListItemAvatar sx={{ minWidth: 'auto', mr: 2 }}>
                      <Avatar
                        src={community.profilePicture}
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: alpha(goldColor, 0.1),
                          color: goldColor,
                          border: `1px solid ${alpha(goldColor, 0.3)}`,
                          fontWeight: 700,
                        }}
                      >
                        {community.name.charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    
                    <Box sx={{ flex: 1, overflow: 'hidden' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.9rem', color: isDark ? '#fff' : deepGreen, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        wfh/{community.slug}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontSize: '0.75rem' }}>
                          {community.membersCount.toLocaleString()} members
                        </Typography>
                        <Chip
                          label={`+${community.growth}%`}
                          size="small"
                          sx={{
                            bgcolor: alpha(goldColor, 0.1),
                            color: goldColor,
                            fontWeight: 700,
                            height: '18px',
                            fontSize: '0.65rem'
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </ListItem>
              ))}
            </List>

            {trendingCommunities.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Groups sx={{ fontSize: 30, color: goldColor, mb: 1, opacity: 0.5 }} />
                <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', mb: 2 }}>
                  No communities yet
                </Typography>
              </Box>
            ) : (
              <Button
                fullWidth
                endIcon={<ArrowForward />}
                onClick={handleViewAllTrending}
                sx={{
                  mt: 2,
                  borderRadius: '12px',
                  bgcolor: alpha(goldColor, 0.1),
                  color: goldColor,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.85rem',
                  py: 1,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: alpha(goldColor, 0.2),
                    transform: 'translateY(-1px)',
                  }
                }}
              >
                View All ({allTrendingCommunities.length})
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Top Contributors Card */}
        <Card sx={{ borderRadius: '24px', ...glassEffect, width: '100%' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1.1rem', md: '0.95rem', lg: '1.1rem' }, color: isDark ? '#fff' : deepGreen, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', '& > svg': { flexShrink: 0 } }}>
                <Star sx={{ color: goldColor }} />
                Top Contributors
              </Typography>
            </Box>

            <List disablePadding>
              {topContributors.map((contributor, index) => (
                <ListItem
                  key={contributor.id}
                  sx={{ 
                    px: 1, 
                    py: 1.5,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                    }
                  }}
                  onClick={(e) => handleUserProfileClick(contributor.username, e)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        mr: 2,
                        color: isDark ? goldColor : deepGreen,
                        minWidth: '20px',
                        textAlign: 'center',
                        fontSize: '1rem',
                        fontWeight: 900
                      }}
                    >
                      {index + 1}
                    </Typography>
                    
                    <ListItemAvatar sx={{ minWidth: 'auto', mr: 2 }}>
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUserProfileClick(contributor.username);
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <Avatar
                          src={contributor.profilePicture}
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: goldColor,
                            color: deepGreen,
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            transition: 'transform 0.2s ease',
                            '&:hover': {
                              transform: 'scale(1.1)',
                            }
                          }}
                        >
                          {contributor.username.charAt(0).toUpperCase()}
                        </Avatar>
                      </Box>
                    </ListItemAvatar>
                    
                    <Box 
                      sx={{
                        flex: 1,
                        // Without this a long username cannot shrink and pushes
                        // the card past the column edge.
                        minWidth: 0,
                        cursor: 'pointer',
                        '&:hover .user-name': {
                          color: goldColor,
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUserProfileClick(contributor.username);
                      }}
                    >
                      <Typography 
                        className="user-name"
                        variant="subtitle2" 
                        sx={{ 
                          fontWeight: 600,
                          mb: 0.5,
                          fontSize: '0.9rem',
                          color: isDark ? '#fff' : deepGreen,
                          transition: 'color 0.2s ease',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {contributor.username}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          icon={<Whatshot sx={{ fontSize: '12px !important' }} />}
                          label={`${contributor.postsCount} posts`}
                          size="small"
                          sx={{
                            bgcolor: alpha('#FF6B6B', 0.1),
                            color: '#FF6B6B',
                            fontWeight: 600,
                            height: '20px',
                            fontSize: '0.7rem',
                            '& .MuiChip-icon': { color: '#FF6B6B' }
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </ListItem>
              ))}
            </List>

            {topContributors.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <People sx={{ fontSize: 30, color: goldColor, mb: 1, opacity: 0.5 }} />
                <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
                  No users yet
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* NEW: Minimal Chat Section */}
        <Card 
          onClick={handleChatCardClick}
          sx={{ 
            borderRadius: '24px', 
            overflow: 'hidden',
            position: 'relative',
            minHeight: 160,
            backgroundImage: `url(${CommunityChatImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            border: isDark 
              ? '1px solid rgba(196, 182, 111, 0.3)' 
              : '1px solid rgba(5, 24, 20, 0.2)',
            boxShadow: isDark 
              ? '0 20px 60px rgba(0,0,0,0.7)' 
              : '0 20px 60px rgba(5, 24, 20, 0.1)',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: isDark 
                ? '0 25px 70px rgba(0,0,0,0.8)' 
                : '0 25px 70px rgba(5, 24, 20, 0.15)',
            }
          }}
        >
          <CardContent sx={{ 
            p: 3, 
            position: 'relative',
            zIndex: 1,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center'
          }}>
            <Typography 
              variant="h1" 
              sx={{ 
                fontWeight: 900,
                fontSize: '2.8rem',
                fontStyle: 'oblique',
                color: '#ffffff',
                textTransform: 'uppercase',
                letterSpacing: '2px',
                textShadow: '0 2px 10px rgba(0,0,0,0.5)',
                pt: 6
              }}
            >
              Chat
            </Typography>

            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 500,
                fontSize: '1.2rem',
                fontStyle: 'oblique',
                color: 'rgba(255,255,255,0.9)',
                textShadow: '0 1px 5px rgba(0,0,0,0.5)',
                mb: 4
              }}
            >
              Join the conversation
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Top Communities List Modal */}
      <TopCommunitiesListModal
        open={viewAllOpen}
        onClose={handleCloseViewAll}
        communities={allTrendingCommunities}
        loading={loading}
      />

      {/* Login Required Modal */}
      <LoginRequiredModal 
        open={loginModalOpen} 
        onClose={() => setLoginModalOpen(false)} 
      />
      
      {/* Community Chat Modal - Only show if user is authenticated */}
      {isAuthenticated && (
        <CommunityChatModal 
          open={chatOpen} 
          onClose={handleCloseChat}
        />
      )}
    </>
  );
};

export default CommunitySidebar;